/**
 * In-memory fakes for the P14-01/02/03 seams.
 *
 * Three behaviours here are not decoration; they are the plan's own acceptance criteria made
 * runnable before the routes exist:
 *
 *   **The dialler is refused before any inbound caller is.** {@link FakeCampaignPort} holds a
 *   tier ceiling and an inbound reserve, and `requestChannelUnit` refuses with
 *   `workspace_at_tier_ceiling` while the reserve would be eaten. The reserve has no default
 *   here either — the constructor requires it — for the same reason
 *   `config.admission.inboundReserve` has none.
 *
 *   **Replacing a question set keeps the old rows.** `replaceQuestionSet` **appends** and
 *   reports `retainedQuestionCount`. Nothing is updated in place, so a response recorded
 *   against question 3 of the old set still points at the words that were read out.
 *
 *   **A skip is a row with a reason.** `recordSkip` writes a `campaign_attempt` whose
 *   `conversationId` is null and whose `skipReason` is set, which is exactly what AC-31's
 *   "records the skip with a reason and reports the skipped rows" asks for.
 */
import { agentVersionId as agentVersionIdCodec, campaignAttemptId as campaignAttemptIdCodec, campaignId as campaignIdCodec, campaignQuestionId as campaignQuestionIdCodec, contactListId as contactListIdCodec, conversationId as conversationIdCodec, } from "../../id/codec.js";
import { CURRENCY_CODE } from "../../billing/money.js";
/** The four columns the design's own CSV is found to contain. */
export const FAKE_CONTACT_COLUMN = Object.freeze([
    { sourceName: "name", role: "display_name", spoken: true, sample: ["Giulia Rossi"] },
    { sourceName: "phone", role: "e164", spoken: false, sample: ["+14155550142"] },
    { sourceName: "last_order", role: "personal_detail", spoken: true, sample: ["Margherita"] },
    { sourceName: "language", role: "language_code", spoken: false, sample: ["it"] },
]);
export class FakeContactListPort {
    #deps;
    #row = [];
    #contact = new Map();
    constructor(deps) {
        this.#deps = deps;
    }
    #of(scope) {
        return this.#row.filter((one) => one.accountId === scope.accountId && one.workspaceId === scope.workspaceId);
    }
    listContactLists(scope) {
        return Promise.resolve(this.#of(scope));
    }
    getContactList(scope, contactListId) {
        const found = this.#of(scope).find((one) => one.contactListId === contactListId);
        if (found === undefined) {
            return Promise.reject(new Error(`No contact_list ${contactListId} in this workspace.`));
        }
        return Promise.resolve(found);
    }
    inspectContactSource(_scope, _source, sourceHandle) {
        return Promise.resolve({
            rowCount: sourceHandle === "" ? 0 : 1_840,
            column: FAKE_CONTACT_COLUMN,
            unreadableRow: [],
        });
    }
    createContactList(scope, draft) {
        const contactListId = contactListIdCodec.next(this.#deps.clock, this.#deps.random);
        const columnMapping = {};
        for (const one of draft.column)
            columnMapping[one.sourceName] = one.role;
        const made = {
            accountId: scope.accountId,
            contactListId,
            workspaceId: scope.workspaceId,
            name: draft.name,
            source: draft.source,
            // The real name comes from `BlobPathHelper.contactList`; a fake must not build one.
            blobName: `${contactListId}.csv`,
            rowCount: 1_840,
            state: "ready",
            columnMapping,
            // Suppression is not an option: it is always honoured, and `decideCampaignDial` does
            // not read this field. It is kept only because the table has it.
            skipSuppressed: true,
            skipCalledWithinDay: draft.skipCalledWithinDay,
            skipAfterNoAnswer: draft.skipAfterNoAnswer,
            createdAt: new Date(this.#deps.clock.now()),
        };
        this.#row.push(made);
        this.#contact.set(contactListId, []);
        return Promise.resolve(made);
    }
    deleteContactList(scope, contactListId) {
        const at = this.#row.findIndex((one) => one.accountId === scope.accountId && one.contactListId === contactListId);
        if (at >= 0)
            this.#row.splice(at, 1);
        this.#contact.delete(contactListId);
        return Promise.resolve();
    }
    listContacts(_scope, contactListId, _cursor) {
        return Promise.resolve({ contact: this.#contact.get(contactListId) ?? [], nextCursor: null });
    }
}
export class FakeCampaignPort {
    #deps;
    #admission;
    #campaign = [];
    #question = new Map();
    #attempt = new Map();
    /** Conversations currently live in the workspace, inbound and outbound together. */
    inUse = 0;
    constructor(deps, admission) {
        this.#deps = deps;
        this.#admission = admission;
    }
    #find(scope, campaignId) {
        const found = this.#campaign.find((one) => one.accountId === scope.accountId &&
            one.workspaceId === scope.workspaceId &&
            one.campaignId === campaignId);
        if (found === undefined)
            throw new Error(`No campaign ${campaignId} in this workspace.`);
        return found;
    }
    #replace(next) {
        const at = this.#campaign.findIndex((one) => one.campaignId === next.campaignId);
        if (at >= 0)
            this.#campaign[at] = next;
        return next;
    }
    #questionRows(scope, campaignId, draft, fromOrdinal) {
        return draft.map((one, index) => ({
            accountId: scope.accountId,
            campaignQuestionId: campaignQuestionIdCodec.next(this.#deps.clock, this.#deps.random),
            campaignId,
            ordinal: fromOrdinal + index + 1,
            code: one.code,
            text: one.text,
            kind: one.kind,
            option: one.option,
            answeredCount: 0,
        }));
    }
    listCampaigns(scope, voiceAgentId) {
        return Promise.resolve(this.#campaign.filter((one) => one.accountId === scope.accountId &&
            one.workspaceId === scope.workspaceId &&
            one.voiceAgentId === voiceAgentId));
    }
    getCampaign(scope, campaignId) {
        return Promise.resolve(this.#find(scope, campaignId));
    }
    listCampaignQuestions(scope, campaignId) {
        this.#find(scope, campaignId);
        return Promise.resolve(this.#question.get(campaignId) ?? []);
    }
    estimateCampaign(_scope, draft) {
        const personCount = 1_840;
        const simultaneousCall = Math.min(draft.simultaneousCall, this.#admission.channelUnitCeiling);
        const dayCount = Math.max(1, draft.dayOfWeek.length);
        // 62% answered, 2.4 minutes each, 50c a minute — the design's own worked arithmetic.
        const centre = Math.round(personCount * 0.62 * 2.4 * 50);
        return Promise.resolve({
            personCount,
            dayCount: Math.max(1, Math.ceil(personCount / Math.max(1, simultaneousCall * dayCount * 26))),
            lowMinor: Math.round(centre * 0.8),
            highMinor: Math.round(centre * 1.25),
            currency: CURRENCY_CODE,
            assumption: "62% answer, 2 minutes 24 seconds each, at 50c a minute.",
            simultaneousCallCeiling: this.#admission.channelUnitCeiling,
        });
    }
    createCampaign(scope, draft) {
        const campaignId = campaignIdCodec.next(this.#deps.clock, this.#deps.random);
        const at = new Date(this.#deps.clock.now());
        const made = {
            accountId: scope.accountId,
            campaignId,
            workspaceId: scope.workspaceId,
            voiceAgentId: draft.voiceAgentId,
            contactListId: draft.contactListId,
            name: draft.name,
            state: draft.startsAt === null ? "running" : "scheduled",
            // In production the factory publishes the question set as an agent_version; the fake
            // mints one so the pointer is a real id of the right brand rather than a cast.
            questionSetAgentVersionId: agentVersionIdCodec.next(this.#deps.clock, this.#deps.random),
            alternateQuestionSetId: null,
            attemptsPerPerson: draft.attemptsPerPerson,
            waitBetweenAttemptMinute: draft.waitBetweenAttemptMinute,
            dayOfWeek: draft.dayOfWeek,
            callFromLocalTime: draft.callFromLocalTime,
            callToLocalTime: draft.callToLocalTime,
            simultaneousCall: Math.min(draft.simultaneousCall, this.#admission.channelUnitCeiling),
            startsAt: draft.startsAt ?? at,
            pacePerMinute: 0,
            estimatedCostMinor: 0,
            currency: CURRENCY_CODE,
            startedAt: draft.startsAt === null ? at : null,
            pausedAt: null,
            finishedAt: null,
        };
        this.#campaign.push(made);
        this.#question.set(campaignId, this.#questionRows(scope, campaignId, draft.question, 0));
        this.#attempt.set(campaignId, []);
        return Promise.resolve(made);
    }
    setCampaignPaused(scope, campaignId, paused) {
        const found = this.#find(scope, campaignId);
        const at = new Date(this.#deps.clock.now());
        return Promise.resolve(this.#replace({
            ...found,
            state: paused ? "paused" : "running",
            pausedAt: paused ? at : null,
        }));
    }
    replaceQuestionSet(scope, campaignId, question) {
        this.#find(scope, campaignId);
        const held = this.#question.get(campaignId) ?? [];
        const retainedQuestionCount = held.length;
        const added = this.#questionRows(scope, campaignId, question, retainedQuestionCount);
        // APPEND. The old rows are not touched, so every campaign_response still points at the
        // wording its answer was given against.
        this.#question.set(campaignId, [...held, ...added]);
        return Promise.resolve({
            campaignId,
            question: added,
            retainedQuestionCount,
            replacedAt: new Date(this.#deps.clock.now()),
        });
    }
    requestChannelUnit(scope, campaignId) {
        this.#find(scope, campaignId);
        const outboundCeiling = this.#admission.channelUnitCeiling - this.#admission.inboundReserve;
        if (this.inUse >= outboundCeiling) {
            return Promise.resolve({ admitted: false, refusalReason: "workspace_at_tier_ceiling" });
        }
        this.inUse += 1;
        return Promise.resolve({
            admitted: true,
            conversationId: conversationIdCodec.next(this.#deps.clock, this.#deps.random),
        });
    }
    recordSkip(scope, campaignId, contactId, skipReason) {
        this.#find(scope, campaignId);
        const held = this.#attempt.get(campaignId) ?? [];
        const made = {
            accountId: scope.accountId,
            campaignAttemptId: campaignAttemptIdCodec.next(this.#deps.clock, this.#deps.random),
            campaignId,
            contactId,
            attemptIndex: held.filter((one) => one.contactId === contactId).length + 1,
            conversationId: null,
            outcome: null,
            skipReason,
            attemptedAt: new Date(this.#deps.clock.now()),
        };
        this.#attempt.set(campaignId, [...held, made]);
        return Promise.resolve(made);
    }
    listSkips(scope, campaignId, _cursor) {
        this.#find(scope, campaignId);
        const skip = [];
        for (const one of this.#attempt.get(campaignId) ?? []) {
            const skipReason = one.skipReason;
            if (skipReason === null)
                continue;
            skip.push({
                campaignAttemptId: one.campaignAttemptId,
                contactId: one.contactId,
                e164: `contact:${one.contactId}`,
                skipReason,
                attemptedAt: one.attemptedAt,
            });
        }
        return Promise.resolve({ skip, nextCursor: null });
    }
}
/**
 * The Autumn survey's own figures, so a screen built against this fake shows the numbers the
 * design was drawn with rather than lorem ipsum.
 */
export class FakeSurveyResultPort {
    #round = [];
    #summary = new Map();
    #question = new Map();
    #respondent = new Map();
    seed(input) {
        this.#round.push(input.round);
        this.#summary.set(input.round.surveyRoundId, input.summary);
        this.#question.set(input.round.surveyRoundId, input.question);
        this.#respondent.set(input.round.surveyRoundId, input.respondent);
    }
    listSurveyRounds(scope, _voiceAgentId) {
        return Promise.resolve(this.#round.filter((one) => one.accountId === scope.accountId && one.workspaceId === scope.workspaceId));
    }
    getSurveySummary(_scope, surveyRoundId) {
        const found = this.#summary.get(surveyRoundId);
        if (found === undefined) {
            return Promise.reject(new Error(`No survey_round ${surveyRoundId} seeded in this fake.`));
        }
        return Promise.resolve(found);
    }
    listQuestionResults(_scope, surveyRoundId) {
        return Promise.resolve(this.#question.get(surveyRoundId) ?? []);
    }
    listRespondents(_scope, surveyRoundId, status, _cursor) {
        const held = this.#respondent.get(surveyRoundId) ?? [];
        return Promise.resolve({
            respondent: status === "all" ? held : held.filter((one) => one.status === status),
            nextCursor: null,
        });
    }
    requestDownload(_scope, request) {
        return Promise.resolve({
            downloadUrl: `https://download.wetalk.example/survey/${request.surveyRoundId}.${request.format}`,
            expiresAt: new Date(Date.UTC(2026, 8, 22, 23, 59, 59)),
        });
    }
}
//# sourceMappingURL=fake-outbound.js.map